import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAppContext } from '@/hooks/useAppContext';

interface LanguagePickerProps {
  className?: string;
}

// Top 20 major world languages by total speakers
const languages = [
  { code: 'system', flag: null, name: 'systemLanguage' },
  { code: 'en', flag: '🇺🇸', name: 'English' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
  { code: 'hi', flag: '🇮🇳', name: 'हिन्दी' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'ar', flag: '🇸🇦', name: 'العربية' },
  { code: 'bn', flag: '🇧🇩', name: 'বাংলা' },
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'ja', flag: '🇯🇵', name: '日本語' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'ko', flag: '🇰🇷', name: '한국어' },
  { code: 'vi', flag: '🇻🇳', name: 'Tiếng Việt' },
  { code: 'it', flag: '🇮🇹', name: 'Italiano' },
  { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
  { code: 'pl', flag: '🇵🇱', name: 'Polski' },
  { code: 'nl', flag: '🇳🇱', name: 'Nederlands' },
  { code: 'id', flag: '🇮🇩', name: 'Bahasa Indonesia' },
  { code: 'th', flag: '🇹🇭', name: 'ไทย' },
  { code: 'uk', flag: '🇺🇦', name: 'Українська' },
] as const;

export function LanguagePicker({ className }: LanguagePickerProps) {
  const { t } = useTranslation();
  const { config, updateConfig } = useAppContext();

  const handleLanguageChange = (language: string) => {
    updateConfig((prev) => ({ ...prev, language: language === 'system' ? undefined : language }));
  };

  return (
    <Select value={config.language ?? 'system'} onValueChange={handleLanguageChange}>
      <SelectTrigger className={className}>
        <div className="flex items-center gap-2">
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        {languages.map((lang) => (
          <SelectItem key={lang.code} value={lang.code}>
            <div className="flex items-center gap-2">
              {lang.flag ? (
                <span className="text-lg">{lang.flag}</span>
              ) : (
                <Languages className="h-4 w-4" />
              )}
              {lang.code === 'system' ? t('systemLanguage') : lang.name}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
